import React from 'react';
import Skeleton from '../../../styled components/Skeleton';
import { BookingCard } from './ListViewCard/ListViewCard.style';
import type { SkeletonWidth } from '../../../styled components/Skeleton';

/* Three cards is enough to read as "a list of cards is coming" without
   pretending to know how many there will be */
const placeholderCards = [0, 1, 2];

/* Cycled rather than uniform, so the placeholders read as text of differing
   lengths instead of a stack of identical bars */
const titleWidths: SkeletonWidth[] = ['1/2', '2/3', '1/3'];
const valueWidths: SkeletonWidth[] = ['1/2', '1/3', '2/3', '1/2'];

interface ListViewCardsLoadingProps {
  /* How many detail rows each placeholder card gets — the number of columns
     the loaded cards will show, so the placeholder is about the right height */
  detailRowsCount: number;
}

/* The card view while its bookings are being fetched: the cards the view is
 * about to show, in their own shape, with skeletons where their text will be.
 *
 * The card view has no header or columns it could show without data — unlike
 * the table — so a spinner would leave it empty. This keeps the page the size
 * and shape it will have once the bookings land. */
const ListViewCardsLoading = ({ detailRowsCount }: ListViewCardsLoadingProps) => (
  <section
    tw="mb-16"
    role="status"
    aria-label={i18n('Loading…')}
    data-test-list-view-cards-loading
  >
    {/* Stands in for the day heading the cards are grouped under */}
    <Skeleton $textSize="subhead" $width="1/3" />
    <div tw="mt-8">
      {placeholderCards.map((cardIndex) => (
        <BookingCard key={cardIndex} $padding={16} $status={null}>
          <Skeleton
            $textSize="body"
            $width={titleWidths[cardIndex % titleWidths.length]}
          />
          <dl tw="mt-4 mb-0">
            {Array.from({ length: detailRowsCount }, (unused, rowIndex) => (
              <div tw="flex items-center mt-2" key={rowIndex}>
                <Skeleton as="dt" $textSize="meta" $width="1/4" tw="mr-8" />
                <Skeleton
                  as="dd"
                  $textSize="body"
                  $width={valueWidths[rowIndex % valueWidths.length]}
                />
              </div>
            ))}
          </dl>
        </BookingCard>
      ))}
    </div>
  </section>
);

export default ListViewCardsLoading;
